import React from 'react';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import { extractSnippet, highlightTerms } from './searchUtils';

interface ChunkSearchResult {
  documentId: string;
  pageNumber: number | null;
  endPageNumber: number | null;
  content: string;
  chunkIndex: number;
  score: number;
  hasKeywordMatch: boolean;
}

interface SearchResultItemProps {
  chunk: ChunkSearchResult;
  query: string;
  onClick: () => void;
}

export const SearchResultItem: React.FC<SearchResultItemProps> = ({
  chunk,
  query,
  onClick,
}) => {
  const snippet = extractSnippet(chunk.content, query, 150);

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-start gap-2.5 px-3 py-3 hover:bg-cyan-50/60 dark:hover:bg-cyan-900/15 transition-colors group"
    >
      {/* Page badge */}
      <span
        className={clsx(
          'shrink-0 mt-px inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums',
          chunk.pageNumber != null
            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
            : 'bg-medical-100 text-medical-500 dark:bg-medical-700 dark:text-medical-400',
        )}
      >
        {chunk.pageNumber != null ? `p.${chunk.pageNumber}` : '#'}
      </span>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className="text-xs text-medical-700 dark:text-medical-300 leading-relaxed line-clamp-2">
          {chunk.hasKeywordMatch
            ? highlightTerms(snippet, query)
            : snippet}
        </p>
        <div className="flex items-center gap-2 mt-1.5">
          {/* Relevance bar */}
          <div className="flex items-center gap-1">
            <div
              className={clsx(
                'h-1 rounded-full',
                chunk.score >= 0.7
                  ? 'bg-green-500 w-6'
                  : chunk.score >= 0.4
                    ? 'bg-amber-500 w-4'
                    : 'bg-medical-300 dark:bg-medical-600 w-2',
              )}
            />
            <span className="text-[9px] text-medical-400 tabular-nums">
              {Math.round(chunk.score * 100)}%
            </span>
          </div>
          {!chunk.hasKeywordMatch && (
            <span className="text-[9px] text-primary-400 dark:text-primary-500 italic">
              semantic match
            </span>
          )}
        </div>
      </div>

      {/* Hover chevron */}
      <ChevronRight className="shrink-0 h-3.5 w-3.5 text-medical-300 dark:text-medical-600 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};
